import {
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  format as formatDate,
  parse,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import creditCardType from "credit-card-type";
import { CreditCardTypeCardBrandId } from "credit-card-type/dist/types";
import { ProductConstants } from "@@types/IProduct";

export function cellphone(v: string) {
  v = v.replace(/\D/g, ""); //Remove tudo o que não é dígito
  v = v.replace(/^(\d\d)(\d)/g, "($1) $2"); //Coloca parênteses em volta dos dois primeiros dígitos
  v = v.replace(/(\d{4})(\d)/, "$1-$2"); //Coloca hífen entre o quarto e o quinto dígitos
  return v || "";
}

export function formatBrazilianPhone(phone: string) {
  const countryCode = phone.slice(0, 2);
  if (countryCode === "55") {
    return phone.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, "$1 $2 $3-$4");
  } else {
    return phone;
  }
}

export const regexOneLetter = new RegExp("^(?=.*[A-Za-z])");

export const regexEightChar = new RegExp("^(?=.{8,})");
export const regexCapitalLowerLetters = new RegExp("^(?=.*[A-Z][a-z]*)");
export const regexAlphanumeric = new RegExp(`^(?![A-Za-z]+$)[0-9A-Za-z]+$`);

export const regexOneNumber = /.*\d.*/; // Regex para verificar se há pelo menos um número na senha
export const regexSpecialChar = /[~`¿¡!#$%\^&*€£@+÷=\-\[\]\\';,/{}\(\)|\\":<>\?\.\_]/; // Regex para verificar se há pelo menos um caractere especial na senha
export const regexCapitalLetters = /.*[A-Z].*/; // Regex para verificar se há pelo menos uma letra maiúscula na senha
export const regexLowerLetters = /.*[a-z].*/; // Regex para verificar se há pelo menos uma letra minúscula na senha

export const regexCurrency = new RegExp(`^[0-9]{1,3}(\.[0-9]{3})*,[0-9]{2}$`);

export const regexDate = new RegExp(`^(0[1-9]|[12][0-9]|3[01])[/](0[1-9]|1[012])[/](19|20)[0-9]{2}$`);

export const regexEmail = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

export function getRandomInt(max: number) {
  return Math.floor(Math.random() * max);
}

const cpfPattern = [/\d/, /\d/, /\d/, ".", /\d/, /\d/, /\d/, ".", /\d/, /\d/, /\d/, "-", /\d/, /\d/];
const cnpjPattern = [
  /\d/,
  /\d/,
  ".",
  /\d/,
  /\d/,
  /\d/,
  ".",
  /\d/,
  /\d/,
  /\d/,
  "/",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  "-",
  /\d/,
  /\d/,
];

export const masks = {
  percent: [/\d/, /\d/, /\d/],
  phone: ["(", /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
  zipCode: [/\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/],
  date: [/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/],
  cpf: cpfPattern,
  cnpj: cnpjPattern,
  document: (rawValue: string) => {
    return rawValue.length > 14 ? cnpjPattern : cpfPattern;
  },
  creditCard: [
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    " ",
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    " ",
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    " ",
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    " ",
  ],
  cvv: [/\d/, /\d/, /\d/],
  expiration: (rawValue: string) => {
    const currentYear = new Date().getFullYear().toString();

    const secondDigitMonth = rawValue[0] == "1" ? /[0-2]/ : /[1-9]/;
    const secondDigitYear = rawValue[3] == currentYear.charAt(2) ? new RegExp(`[${currentYear.charAt(3)}-9]`) : /[0-9]/;

    return [/[0-1]/, secondDigitMonth, "/", new RegExp(`[${currentYear.charAt(2)}-9]`), secondDigitYear];
  },
  number: createNumberMask({
    prefix: "",
    includeThousandsSeparator: false,
  }),
  currencyBRL: createNumberMask({
    prefix: "R$ ",
    suffix: "",
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ".",
    allowDecimal: true,
    decimalSymbol: ",",
    decimalLimit: 2, // how many digits allowed after the decimal
    integerLimit: 7, // limit length of integer numbers
    allowNegative: false,
    allowLeadingZeroes: false,
    requireDecimal: false,
  }),
};

export const format = {
  number: (rawValue: number) =>
    Intl.NumberFormat("pt-BR", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      rawValue
    ),

  money: (rawValue: number) =>
    Number.isInteger(rawValue)
      ? Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(rawValue / 100)
      : "...",

  moneyUnsigned: (rawValue: number) =>
    (rawValue / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),

  cpf: (rawValue: string) => rawValue?.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"),

  cnpj: (rawValue: string) => rawValue?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5"),

  onlyNumbers: (rawValue: string) => rawValue?.replace(/[^0-9]+/g, ""),

  initials: (rawValue?: string) => {
    const name = rawValue?.trim() || "";

    const words = name.split(" ").map(w => w.trim()) || [];

    const initials = (
      words.length > 1 ? words[0].substring(0, 1) + words[words.length - 1].substring(0, 1) : name.substring(0, 2)
    ).toUpperCase();

    return initials;
  },

  localeNumber: (rawValue: number) => {
    return rawValue.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).replace(",", ".");
  },

  obscureEmail: (rawValue: string, length: number = 5) => {
    const [name, domain] = rawValue.split("@");
    const half = Math.floor(name.length / 2);
    const size = half <= length ? half : length;
    return `${name.substring(0, size)}${new Array(name.length - size + 1).join("*")}@${domain}`;
  },

  capitalize: (rawValue: string) => rawValue?.charAt(0).toUpperCase() + rawValue?.slice(1).toLowerCase(),

  capitalizeEachWord: (rawValue?: string) =>
    rawValue
      ?.split(" ")
      .map(w => `${w.charAt(0).toUpperCase()}${w.slice(1).toLowerCase()}`)
      .join(" "),

  lastFourDigits: (rawValue: string) => (rawValue.length ? rawValue.substring(rawValue.length - 4) : rawValue),

  zipCode: (rawValue: string) => rawValue?.replace(/^(\d{5})(\d{3})/, "$1-$2"),

  phoneNumber: (rawValue: string) => rawValue?.replace(/^(\d{2})(\d{2})(\d{5})(\d{4})/, "+$1 ($2) $3-$4"),

  dateToString: (date: Date, format: string): string => {
    return formatDate(date, format, { locale: ptBR });
  },

  stringToDate: (date: string, format: string): Date => {
    return parse(date, format, new Date());
  },

  timeAgo: (rawValue: Date): string => {
    const today = endOfDay(new Date());

    const years = differenceInYears(today, rawValue);
    const months = differenceInMonths(today, rawValue);
    const weeks = differenceInWeeks(today, rawValue);
    const days = differenceInDays(today, rawValue);

    if (years > 0) {
      return `Há ${years} ${years > 1 ? "anos" : "ano"}`;
    } else if (months > 0) {
      return `Há ${months} ${months > 1 ? "meses" : "mês"}`;
    } else if (weeks > 0) {
      return `Há ${weeks} ${weeks > 1 ? "semanas" : "semana"}`;
    } else if (days > 1) {
      return `Há ${days} dias`;
    } else {
      return days === 1 ? "Ontem" : "Hoje";
    }
  },

  dateFilter: (rawValue: [Date, Date]): string => {
    const startDate = formatDate(rawValue[0], "yyyy-MM-dd");
    const endDate = formatDate(rawValue[1], "yyyy-MM-dd");
    return startDate + "," + endDate;
  },

  valueFilter: (rawValue: string): number | undefined => {
    const value = parseInt(rawValue.replace(/\D/g, ""));
    return value > 0 ? value : undefined;
  },

  secondsToMinutes: (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : `${remainingSeconds}`;
    return `${formattedMinutes}:${formattedSeconds}`;
  },
};

const toFixedNoRounded = (n: number, fixed: number): number => ~~(Math.pow(10, fixed) * n) / Math.pow(10, fixed);

export function kFormatter(num: number) {
  return Math.abs(num) > 999
    ? toFixedNoRounded(Math.sign(num) * (Math.abs(num) / 1000), 1) + "K"
    : Math.sign(num) * Math.abs(num);
}

export function fileSizeFormatter(num: number, digits: number) {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "kb" },
    { value: 1e6, symbol: "MB" },
    { value: 1e9, symbol: "GB" },
    { value: 1e12, symbol: "TB" },
    { value: 1e15, symbol: "PB" },
    { value: 1e18, symbol: "EB" },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
}

export const formatCnpj = (cnpj: string) => {
  try {
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "$1.$2.$3/$4-$5");
  } catch (err) {
    return cnpj;
  }
};

export function getIntFromMaskedInputCurrency(value: string) {
  const array = value.split(",");
  let newValue = "";

  if (array.length > 1 && array[1] !== "") {
    if (array[1].length == 1) {
      newValue = value + "0";
    } else {
      newValue = value;
    }
  } else {
    newValue = value;
  }
  return parseInt(newValue.replace(/\D/g, ""));
}

export function parseIntToFloat(value: number) {
  const inputInteger = value;
  const lastTwoDigits = inputInteger % 100;
  const parsedFloat = Math.floor(inputInteger / 100) + lastTwoDigits / 100;

  return parsedFloat;
}

export function parseFloatToStringWithDecimals(value: number) {
  const inputFloat = value;
  const parsedString = inputFloat.toFixed(2).replace(".", ",");

  return parsedString;
}

export function parseToInt(value?: string) {
  return value ? parseInt(value) : 0;
}

export function limitStringSize(size: number, string?: string) {
  if (string && string.length > size) {
    return `${string.slice(0, size)}...`;
  }
  return string;
}

export const isLocal = () => process.env.NEXT_PUBLIC_APP_ENV === "local";
export const isStaging = () => process.env.NEXT_PUBLIC_APP_ENV === "staging";
export const isProduction = () => process.env.NEXT_PUBLIC_APP_ENV === "production";

export function checkDateBeforeToday(dateString: string | Date) {
  const today = new Date();
  const date = new Date(dateString);

  if (date < today) {
    return true;
  }

  return false;
}

export function isMoreThanAnHourAway(dateString: string | Date) {
  const currentDate = new Date();
  const date = new Date(dateString);
  const minutesDifference = differenceInMinutes(currentDate, date);

  return minutesDifference > 60;
}

export function formatSnakeCaseToTitleCase(text: string) {
  return text
    .toLowerCase()
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function parsePriceToCurrencyInput(price: number) {
  let priceFloat = parseIntToFloat(price);
  var priceString = parseFloatToStringWithDecimals(priceFloat);
  return priceString;
}

export type CreditCardBrandId =
  | Exclude<CreditCardTypeCardBrandId, "american-express" | "diners-club">
  | "amex"
  | "diners";

export async function getCardBrand(cardNumber: number) {
  const result = await creditCardType(cardNumber.toString())[0];
  return !result
    ? null
    : result?.type === "american-express"
    ? "amex"
    : result?.type === "diners-club"
    ? "diners"
    : result.type;
}

export const isCommunity = (format?: string) =>
  !!format && ["community", "comunidade", "Comunidade", "Curso"].includes(format);
export const isFiles = (format?: string) =>
  !!format && ["file", "arquivos", "Arquivos", "E-book e arquivos"].includes(format);
export const isService = (format?: string) =>
  !!format && ["service", "Serviço", "servicos", "Serviços"].includes(format);

export const getProductType = (productFormat: string): ProductConstants.Type | undefined => {
  if (isService(productFormat)) {
    return ProductConstants.Type.SERVICE;
  }
  if (isFiles(productFormat)) {
    return ProductConstants.Type.FILES;
  }
  if (isCommunity(productFormat)) {
    return ProductConstants.Type.COURSE;
  }

  return undefined;
};

export function removeHtmlTags(str: string) {
  const tagHTMLRegex = new RegExp(/<\/?[^>]+(>|$)/g);
  return str.replace(tagHTMLRegex, "");
}
